// src/features/mech/plastics/playbook/PlasticsPlayBook.jsx
// Plastics Molding-specific PlayBook configuration component

import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import PerlSlider from "../../../../shared/components/PerlSlider";
import PerlTextField from "../../../../shared/components/PerlTextField";
import { theme } from "../../../../design/theme";

const { perlColors } = theme.custom;

/**
 * CorrugatedPlayBook - Corrugated Box-specific playbook configuration
 * 
 * @param {Object} props
 * @param {Object} props.data - PlayBook data object with costParams: { corrugationRate, ohpRate, blankLengthCm, blankWidthCm, areaNetM2, wastagePercent }
 * @param {Function} props.onChange - Callback: (field, value) => void
 */
export default function LaminatesPlayBook({ data = {}, onChange }) {
  // Extract costParams from data.costParams or data directly (for backward compatibility)
  const costParams = data.costParams || {
    corrugationRate: data.corrugationRate || 0,
    cycleTime: data.cycleTime || 0,
    machineTonnage: data.machineTonnage || 0,
    ohpRate: data.ohpRate || 0,
    blankLengthCm: data.blankLengthCm || 0,
    blankWidthCm: data.blankWidthCm || 0,
    areaNetM2: data.areaNetM2 || 0,
    wastagePercent: data.wastagePercent || 0,
  };

  // Handler for parameter changes - updates costParams structure
  const handleParameterChange = (paramName, value) => {
    if (onChange) {
      // Update the costParams nested structure
      onChange("costParams", {
        ...costParams,
        [paramName]: value,
      });
    }
  };


  return (
    <Box>
      {/* Process Parameters Title */}
      <Typography
        variant="subtitle2"
        fontWeight={600}
        sx={{ mb: 2, color: perlColors.textPrimary }}
      >
        Process Parameters
      </Typography>

      {/* First Row - Geometry Values (Blank Length, Blank Width, Area Net) */}
      <Box
        sx={{
          backgroundColor: perlColors.surface,
          borderRadius: 2,
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 3,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          <Box
            sx={{
              minWidth: 220,
              flex: "1 1 280px",
              maxWidth: "calc((100% - 48px) / 3)",
              mb: 2,
            }}
          >
            <PerlTextField
              label="Blank Length (mm)"
              placeholder="Blank length (mm)"
              value={costParams.blankLengthCm ? (parseFloat(costParams.blankLengthCm) * 10).toFixed(2) : "0.00"}
              type="number"
              onChange={(e) => {
                const newValueMm = parseFloat(e.target.value) || 0;
                const newValueCm = newValueMm / 10; // Convert mm to cm
                handleParameterChange("blankLengthCm", newValueCm);
              }}
            />
          </Box>
          <Box
            sx={{
              minWidth: 220,
              flex: "1 1 280px",
              maxWidth: "calc((100% - 48px) / 3)",
              mb: 2,
            }}
          >
            <PerlTextField
              label="Blank Width (mm)"
              placeholder="Blank width (mm)"
              value={costParams.blankWidthCm ? (parseFloat(costParams.blankWidthCm) * 10).toFixed(2) : "0.00"}
              type="number"
              onChange={(e) => {
                const newValueMm = parseFloat(e.target.value) || 0;
                const newValueCm = newValueMm / 10; // Convert mm to cm
                handleParameterChange("blankWidthCm", newValueCm);
              }}
            />
          </Box>
          <Box
            sx={{
              minWidth: 220,
              flex: "1 1 280px",
              maxWidth: "calc((100% - 48px) / 3)",
              mb: 2,
            }}
          >
            <PerlTextField
              label="Area Net"
              placeholder="Net area (mm²)"
              value={costParams.areaNetM2 ? (parseFloat(costParams.areaNetM2) * 1000000).toFixed(2) : "0.00"}
              type="number"
              onChange={(e) => {
                const newValueMm2 = parseFloat(e.target.value) || 0;
                const newValueM2 = newValueMm2 / 1000000; // Convert mm² to m²
                handleParameterChange("areaNetM2", newValueM2);
              }}
            />
          </Box>
        </Box>

        {/* Second Row - Sliders (Wastage and OHP Rate) */}
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          <Box
            sx={{
              minWidth: 220,
              flex: "1 1 280px",
              maxWidth: "calc((100% - 24px) / 2)",
              mb: 2,
            }}
          >
            <PerlSlider
              label="Wastage"
              value={costParams.wastagePercent ? parseFloat(parseFloat(costParams.wastagePercent).toFixed(2)) : 0}
              min={0}
              max={50}
              step={0.5}
              unit="%"
              onChange={(e, v) => handleParameterChange("wastagePercent", parseFloat(v.toFixed(2)))}
            />
          </Box>
          <Box
            sx={{
              minWidth: 220,
              flex: "1 1 280px",
              maxWidth: "calc((100% - 24px) / 2)",
              mb: 2,
            }}
          >
            <PerlSlider
              label="OHP Rate"
              value={costParams.ohpRate ? parseFloat(parseFloat(costParams.ohpRate).toFixed(2)) : 0}
              min={0}
              max={30}
              step={0.5}
              unit="%"
              onChange={(e, v) => handleParameterChange("ohpRate", parseFloat(v.toFixed(2)))}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
